// © Thibault Sabathier, 2025. Tous droits réservés.
// frontend/src/components/admin/AmbassadorAdminToggle.js
//
// Toggle admin pour le badge ambassadeur d'un membre. Rendu uniquement
// dans les contextes où `isAdmin === true && orgType === 'alumni'` —
// le composant parent doit faire ce gating avant de monter.
//
// Décisions produit V1 (Sprint Vidal 2026-05-23) :
//   - 1 seul bouton qui s'adapte selon l'état :
//       * Si !member.is_ambassador → "Désigner ambassadeur" (ouvre modal)
//       * Si member.is_ambassador  → "Gérer le badge ambassadeur" (modal
//                                     avec titre pré-rempli + bouton Retirer)
//   - Le titre ambassadeur est libre (max 80 chars), pas un ENUM.
//     Datalist HTML propose des suggestions contextuelles ("Promo 2020",
//     "Campus Toulouse"...) sans les imposer.
//   - Reset complet sur "Retirer" : titre et date passent à null en DB
//     (pas de zombie data si on réactive plus tard).

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormInput,
  CFormLabel,
  CAlert,
  CSpinner,
} from '@coreui/react'
import { put } from '../../utils/apiClient'

// Suggestions contextuelles pour le titre (datalist HTML). Pures hints UX,
// l'admin peut taper n'importe quoi (libre VARCHAR 80). Ces valeurs ne sont
// PAS persistées en DB — c'est juste une autocomplete browser-native.
// Clés i18n résolues au render (cf. dashboard:ambassador.suggestions).
const TITLE_SUGGESTION_KEYS = [
  'referent_promo',
  'referent_campus',
  'company_relations',
  'international',
  'referent_sector',
]

const AmbassadorAdminToggle = ({ member, onUpdate, renderTrigger }) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [title, setTitle] = useState(member.ambassador_title || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const isAmbassador = !!member.is_ambassador

  // Sync du state local `title` avec la prop si elle change sans remount
  // (ex: refetch parent qui met à jour member.ambassador_title sans
  // démonter le composant). Sans ça, ouvrir-modifier-fermer-rouvrir
  // afficherait l'ancienne valeur locale au lieu de celle du parent.
  // openModal() re-sync aussi mais c'est défense in depth pour le cas
  // où la prop change pendant que la modal est fermée.
  useEffect(() => {
    setTitle(member.ambassador_title || '')
  }, [member.id, member.ambassador_title])

  const openModal = () => {
    setTitle(member.ambassador_title || '')
    setError(null)
    setShowModal(true)
  }

  const closeModal = () => {
    if (loading) return // pas de fermeture pendant une requête
    setShowModal(false)
  }

  const callApi = async (body) => {
    setLoading(true)
    setError(null)
    try {
      const res = await put(`/members/${member.id}/ambassador`, body)
      if (onUpdate) {
        onUpdate({
          is_ambassador: res.is_ambassador,
          ambassador_title: res.ambassador_title,
          ambassador_since: res.ambassador_since,
        })
      }
      setShowModal(false)
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t('dashboard:ambassador.error_generic')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = () => {
    callApi({
      is_ambassador: true,
      ambassador_title: title.trim().substring(0, 80) || null,
    })
  }

  const handleRemove = () => {
    if (!window.confirm(t('dashboard:ambassador.confirm_remove'))) return
    callApi({ is_ambassador: false })
  }

  const triggerLabel = isAmbassador
    ? t('dashboard:ambassador.manage_button')
    : t('dashboard:ambassador.designate_button')

  // renderTrigger : même mécanisme que les autres actions admin de
  // MemberDetailsCard — permet de regrouper ce déclencheur dans un menu
  // déroulant. Défaut = CButton (comportement historique, encore utilisé si
  // ce composant est monté ailleurs un jour).
  const trigger = renderTrigger ? (
    renderTrigger({ onClick: openModal, label: triggerLabel })
  ) : (
    <CButton
      color={isAmbassador ? 'warning' : 'light'}
      size="sm"
      onClick={openModal}
      title={
        isAmbassador
          ? t('dashboard:ambassador.manage_tooltip')
          : t('dashboard:ambassador.designate_tooltip')
      }
      // z-index + position pour passer au-dessus de l'overlay décoratif du
      // header de la fiche profil (pattern bleu pointillé). Sans ça, le
      // bouton est "sous" l'overlay → non cliquable. Cohérent avec le
      // bouton "Envoyer un message" voisin qui a le même fix.
      // Le variant outline a été retiré : sur fond sombre du header,
      // outline + color=light rendait le bouton quasi invisible
      // (border blanc + texte blanc + fond transparent). Solid color
      // donne un encadré clair visible (cohérent avec "Envoyer un
      // message" qui est aussi solid light).
      style={{
        zIndex: 10,
        position: 'relative',
      }}
    >
      {triggerLabel}
    </CButton>
  )

  return (
    <>
      {trigger}

      <CModal visible={showModal} onClose={closeModal} backdrop="static">
        <CModalHeader>
          <CModalTitle>
            {isAmbassador
              ? t('dashboard:ambassador.manage_modal_title')
              : t('dashboard:ambassador.designate_modal_title')}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error && (
            <CAlert color="danger" className="py-2 mb-3" style={{ fontSize: '0.9rem' }}>
              {error}
            </CAlert>
          )}
          <p className="text-medium-emphasis mb-3" style={{ fontSize: '0.9rem' }}>
            {isAmbassador
              ? t('dashboard:ambassador.manage_intro', {
                  name: `${member.firstName} ${member.lastName}`,
                })
              : t('dashboard:ambassador.designate_intro', {
                  name: `${member.firstName} ${member.lastName}`,
                })}
          </p>
          <CFormLabel htmlFor="ambassador-title">
            {t('dashboard:ambassador.title_label')}
          </CFormLabel>
          <CFormInput
            id="ambassador-title"
            list="ambassador-title-suggestions"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('dashboard:ambassador.title_placeholder')}
            maxLength={80}
            disabled={loading}
          />
          <datalist id="ambassador-title-suggestions">
            {TITLE_SUGGESTION_KEYS.map((k) => (
              <option key={k} value={t(`dashboard:ambassador.suggestions.${k}`)} />
            ))}
          </datalist>
          <small className="text-medium-emphasis d-block mt-1">
            {t('dashboard:ambassador.title_help')}
          </small>
        </CModalBody>
        <CModalFooter>
          {isAmbassador && (
            <CButton color="danger" variant="outline" onClick={handleRemove} disabled={loading}>
              {t('dashboard:ambassador.remove_badge')}
            </CButton>
          )}
          <CButton color="secondary" variant="outline" onClick={closeModal} disabled={loading}>
            {t('dashboard:ambassador.cancel')}
          </CButton>
          <CButton color="warning" onClick={handleSave} disabled={loading}>
            {loading ? (
              <CSpinner size="sm" />
            ) : isAmbassador ? (
              t('dashboard:ambassador.update')
            ) : (
              t('dashboard:ambassador.designate_submit')
            )}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AmbassadorAdminToggle
